type Promotion = {
  id: string
  locations?: string[]
  pickupType?: string | null
  weekdays?: string[]
  startTime?: string | null
  endTime?: string | null
  minExpense?: number | null
  customerIds?: string[]
  timesPerPerson?: number
}

type PromotionWithValidation = Promotion & {
  isValid: boolean
  isVisible: boolean
}

export default class PromotionFilter {
  expense: number
  locationId?: string
  weekday?: string
  pickupType?: string
  customer?: string
  usedPromotions?: Record<string, number>

  constructor(
    expense: number,
    locationId?: string,
    weekday?: string,
    pickupType?: string,
    customer?: string,
    usedPromotions?: Record<string, number>
  ) {
    this.expense = expense
    this.locationId = locationId
    this.weekday = weekday
    this.pickupType = pickupType
    this.customer = customer
    this.usedPromotions = usedPromotions
  }

  addValidation<T extends Promotion>(
    promotions: T[],
    doCustomerChecks = true
  ): (T & PromotionWithValidation)[] {
    return promotions.map(promotion => {
      return {
        ...promotion,
        isValid: this.isPromotionValid(promotion, doCustomerChecks),
        isVisible: this.isPromotionVisible(promotion)
      }
    })
  }

  isPromotionVisible(promotion?: Promotion): boolean {
    if (!promotion) return false

    if ((promotion.locations?.length || 0) > 0) {
      if (!this.locationId || !promotion.locations!.includes(this.locationId)) return false
    }

    if (promotion.pickupType && promotion.pickupType !== this.pickupType) return false

    if ((promotion.weekdays?.length || 0) > 0) {
      if (!promotion.weekdays!.includes(this.weekday!)) return false
    }

    if (promotion.startTime && new Date() < new Date(promotion.startTime)) return false

    if (promotion.endTime && new Date() > new Date(promotion.endTime)) return false

    return true
  }

  isPromotionValid(promotion?: Promotion, doCustomerChecks = true): boolean {
    if (!promotion) return false

    if (!this.isPromotionVisible(promotion)) return false

    if (this.expense < (promotion.minExpense || 0)) return false

    if (doCustomerChecks) {
      if (
        (promotion.customerIds?.length || 0) > 0 &&
        !promotion.customerIds!.includes(this.customer!)
      ) {
        return false
      }

      if (promotion.timesPerPerson) {
        if (!this.customer) return false

        const promotionUsages = this.usedPromotions?.[promotion.id]
        if (promotionUsages && promotionUsages >= promotion.timesPerPerson) return false
      }
    }

    return true
  }
}
